import { createSlice } from '@reduxjs/toolkit';
import { initiateInvoicePaymentThunk } from './thunks/initiateInvoicePayment.thunk';
import { getInvoicePaymentThunk } from './thunks/getInvoicePayment.thunk';
import { completeInvoicePaymentThunk } from './thunks/completeInvoicePayment.thunk';

export {
  initiateInvoicePaymentThunk,
  getInvoicePaymentThunk,
  completeInvoicePaymentThunk,
};

/**
 * Payments are keyed by invoice rather than held as a single "current payment":
 * the invoice list can show several dialogs' worth of state, and a payment that
 * settles hours later still belongs to its invoice.
 */
const paymentsSlice = createSlice({
  name: 'payments',
  initialState: {
    byInvoiceId: {},
    initiating: false,
    error: null,
  },
  reducers: {
    clearPaymentError(state) {
      state.error = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(initiateInvoicePaymentThunk.pending, (state) => {
        state.initiating = true;
        state.error = null;
      })
      .addCase(initiateInvoicePaymentThunk.fulfilled, (state, action) => {
        state.initiating = false;
        const { invoiceId } = action.meta.arg;
        state.byInvoiceId[invoiceId] = action.payload;
      })
      .addCase(initiateInvoicePaymentThunk.rejected, (state, action) => {
        state.initiating = false;
        state.error = action.payload ?? { message: action.error?.message };
      })

      .addCase(getInvoicePaymentThunk.fulfilled, (state, action) => {
        const { invoiceId } = action.meta.arg;
        // null means the invoice has never been paid; keep the key absent so the
        // UI can tell "no payment" from "payment we have not loaded".
        if (action.payload) {
          state.byInvoiceId[invoiceId] = action.payload;
        } else {
          delete state.byInvoiceId[invoiceId];
        }
      })

      .addCase(completeInvoicePaymentThunk.fulfilled, (state, action) => {
        const { invoiceId } = action.meta.arg;
        state.byInvoiceId[invoiceId] = action.payload;
      });
  },
});

export const { clearPaymentError } = paymentsSlice.actions;

export const selectPaymentInitiating = (state) => state.payments.initiating;
export const selectPaymentError = (state) => state.payments.error;
export const selectPaymentForInvoice = (invoiceId) => (state) =>
  invoiceId ? state.payments.byInvoiceId[invoiceId] : undefined;

export default paymentsSlice.reducer;
